import { addNotification } from "portal/state/notifications";
import {
  Alert,
  Button,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AlmanacUpdate,
  orderCategoriesByRole,
  resolveCategoryRole,
  toRadioChip,
} from "portal/utils/categoryCollection/category";
import {
  arePlantProfilesEqual,
  deletePlantProfileChanges,
  determinePlantProfileChanges,
  isPlantProfileActive,
  isPlantProfileActiveOnFleet,
  revertPlantProfileChanges,
  toUnsavedPlantProfile,
  tryGetResettableAlmanacBaseline,
  UnsavedPlantProfile,
} from "portal/utils/categoryCollection/plantProfile";
import { asOneOf, useQuery } from "portal/utils/hooks/useQuery";
import { CategoryCollectionCustomerProvider } from "./CategoryCollectionCustomerContext";
import { CLIENT_ERROR_CODES } from "portal/utils/errors";
import { ConfirmationDialog } from "../ConfirmationDialog";
import { convert } from "portal/utils/units/units";
import { findWhere } from "portal/utils/arrays";
import { FloatingSaveButton } from "./FloatingSaveButton";
import {
  getAdminPlantProfilePath,
  getCustomerPlantProfilePath,
  getPlantProfileEditPath,
  getRobotActivePlantProfilePath,
  isPlantProfileEditPath,
  TAB_QUERY,
} from "portal/utils/routing";
import {
  imagesToCategoryIds,
  plantProfileImageToggle,
} from "portal/utils/categoryCollection/image";
import { isEqual } from "portal/utils/equality";
import { isNil, isUndefined } from "portal/utils/identity";
import { isValidAlmanac } from "portal/utils/shootTime";
import { Loading } from "../Loading";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { OutOfDateAlert } from "./OutOfDateAlert";
import { PlantProfileChanges } from "protos/portal/category_profile";
import {
  PlantProfileEdit,
  PlantProfileEditableHeader,
} from "./tabs/PlantProfileEdit";
import { PlantProfileRoleIcon } from "./PlantProfileRoleIcon";
import {
  PlantProfileTab,
  resolvePlantProfileTab,
  tabForPlantProfileViewLeave,
  withPlantProfileTabQuery,
} from "./tabs/tabs";
import { PlantProfileView } from "./tabs/PlantProfileView";
import { portalEndpoints } from "portal/state/portalApi";
import { RadioChipOption } from "../RadioChips";
import { RobotSummaryResponse } from "protos/portal/robots";
import { SavedPlantProfile } from "protos/robot_syncer/plant_profile";
import { skipToken } from "@reduxjs/toolkit/query";
import { UnsavedChangesDiscardDialog } from "portal/components/unsavedChanges/UnsavedChangesDiscardDialog";
import {
  useCanReadPlantProfile,
  useCanUpdatePlantProfile,
} from "portal/views/plantProfiles/permissions";
import { useDispatch } from "react-redux";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { useUnsavedChangesBlocker } from "portal/components/unsavedChanges/useUnsavedChangesBlocker";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/ModeEditOutlined";
import ParentIcon from "@mui/icons-material/ArrowBackOutlined";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import SaveIcon from "@mui/icons-material/SaveOutlined";

type PlantProfileProps = {
  uuid: string;
  parentLink?: string;
  /** getRobot with includeCustomer and includeHealthLogs (`includeDailyMetrics: false`). */
  robotSummary?: RobotSummaryResponse;
} & (
  | {
      isAdmin: true;
      customerUuid?: never;
      useSetMutation: typeof portalEndpoints.applyGlobalPlantProfileChanges.useMutation;
    }
  | {
      isAdmin?: false;
      customerUuid: string;
      useSetMutation: typeof portalEndpoints.applyCustomerPlantProfileChanges.useMutation;
    }
);

const PLANT_PROFILE_QUERY_OPTIONS = {
  pollingInterval: convert(60).from("s").to("ms"),
  skipPollingIfUnfocused: true,
  refetchOnFocus: true,
};

type ApplyPlantProfileChangesInput =
  | { ccUUID: string; changes: PlantProfileChanges }
  | { customerUuid?: string; ccUUID: string; changes: PlantProfileChanges };

export const PlantProfile: FC<PlantProfileProps> = ({
  uuid,
  isAdmin = false,
  customerUuid,
  robotSummary,
  parentLink,
  useSetMutation,
}) => {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const toApplyChangesInput = useCallback(
    (
      ccUUID: string,
      changes: PlantProfileChanges
    ): ApplyPlantProfileChangesInput =>
      isAdmin || !customerUuid
        ? { ccUUID, changes }
        : { ccUUID, customerUuid, changes },
    [isAdmin, customerUuid]
  );

  const globalPlantProfileQuery = useQueryPopups(
    portalEndpoints.getGlobalPlantProfile.useQuery(
      isAdmin && uuid ? { uuid } : skipToken,
      PLANT_PROFILE_QUERY_OPTIONS
    )
  );
  const customerPlantProfileQuery = useQueryPopups(
    portalEndpoints.getCustomerPlantProfile.useQuery(
      !isAdmin && uuid && customerUuid ? { uuid, customerUuid } : skipToken,
      PLANT_PROFILE_QUERY_OPTIONS
    )
  );
  const {
    data: latestActivatedPlantProfile,
    isLoading: isPlantProfileLoading,
    isError,
    error,
  } = isAdmin ? globalPlantProfileQuery : customerPlantProfileQuery;

  const shouldScanFleet = Boolean(customerUuid) && !robotSummary;
  const {
    data: robotSummaries,
    isLoading: isFleetLoading,
    isError: isFleetError,
  } = useQueryPopups(
    portalEndpoints.listRobots.useQuery(
      shouldScanFleet ? { excludeMetrics: true } : skipToken
    ),
    { errorVariant: "warning" }
  );

  // server version that the current draft was derived from
  const [draftBaselineProfile, setDraftBaselineProfile] = useState<
    SavedPlantProfile | undefined
  >();
  // local changes
  const [unsavedPlantProfile, setUnsavedPlantProfile] = useState<
    UnsavedPlantProfile | undefined
  >();
  // confirmation dialog
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [pendingLeavePath, setPendingLeavePath] = useState<string | undefined>(
    undefined
  );
  const [activeCategoryId, setActiveCategoryId] = useState<
    string | undefined
  >();
  const [invalidAlmanacFieldIds, setInvalidAlmanacFieldIds] = useState<
    Set<string>
  >(new Set());
  const hasInvalidAlmanacFields = invalidAlmanacFieldIds.size > 0;

  const initializeEditSession = useCallback((saved: SavedPlantProfile) => {
    const draft = toUnsavedPlantProfile(saved);
    if (!draft) {
      return;
    }
    setDraftBaselineProfile(saved);
    // editable copy of profile
    setUnsavedPlantProfile(draft);
    setInvalidAlmanacFieldIds(new Set());
  }, []);

  const clearEditSession = useCallback(() => {
    setDraftBaselineProfile(undefined);
    setUnsavedPlantProfile(undefined);
    setInvalidAlmanacFieldIds(new Set());
  }, []);

  // tabs & navigation
  const isEditing = isPlantProfileEditPath(pathname);
  const robotSerial = robotSummary?.robot?.serial;
  const viewPath = isAdmin
    ? getAdminPlantProfilePath(uuid)
    : robotSerial
    ? getRobotActivePlantProfilePath(robotSerial)
    : getCustomerPlantProfilePath(uuid, customerUuid);
  const editPath = viewPath ? getPlantProfileEditPath(viewPath) : undefined;
  // read-only: View/Edit own the tab UI; parent needs current tab for enter/leave paths
  const [tabParam] = useQuery(
    TAB_QUERY,
    asOneOf(
      PlantProfileTab.ALL,
      PlantProfileTab.CATEGORIZED,
      PlantProfileTab.HISTORY
    )
  );
  const tabForViewLeave = useMemo(
    () =>
      tabForPlantProfileViewLeave(resolvePlantProfileTab(isEditing, tabParam)),
    [isEditing, tabParam]
  );

  if (
    isEditing &&
    latestActivatedPlantProfile?.profile &&
    !unsavedPlantProfile
  ) {
    initializeEditSession(latestActivatedPlantProfile);
  }

  // default All Images chip: first Crop, else first category in role order
  useEffect(() => {
    const viewProfile = latestActivatedPlantProfile
      ? toUnsavedPlantProfile(latestActivatedPlantProfile)
      : undefined;
    const profile = isEditing ? unsavedPlantProfile : viewProfile;
    const categories = profile?.categories ?? [];
    if (!activeCategoryId && categories.length > 0) {
      const ordered = orderCategoriesByRole(
        categories,
        profile?.categoryRoles ?? {}
      );
      setActiveCategoryId(ordered[0]?.id ?? categories[0]?.id);
    }
  }, [
    activeCategoryId,
    isEditing,
    latestActivatedPlantProfile,
    unsavedPlantProfile,
  ]);

  const displayProfile = isEditing
    ? unsavedPlantProfile
    : latestActivatedPlantProfile?.profile
    ? toUnsavedPlantProfile(latestActivatedPlantProfile)
    : undefined;

  const savedPlantProfileForCompare = isEditing
    ? draftBaselineProfile
    : latestActivatedPlantProfile;

  const hasUnsavedChanges = useMemo(() => {
    if (!isEditing || !draftBaselineProfile || !unsavedPlantProfile) {
      return false;
    }
    // converting to saved to unsaved for equality comparison
    return !arePlantProfilesEqual(
      unsavedPlantProfile,
      toUnsavedPlantProfile(draftBaselineProfile)
    );
  }, [draftBaselineProfile, isEditing, unsavedPlantProfile]);
  const {
    allowNextNavigation,
    isBlocked: isNavigationBlocked,
    proceed: proceedWithNavigation,
    reset: resetBlockedNavigation,
  } = useUnsavedChangesBlocker(isEditing && hasUnsavedChanges);

  // navigation (view <-> edit, back, discard)
  const leaveTo = useCallback(
    (path: string) => {
      allowNextNavigation();
      setConfirmDiscard(false);
      setPendingLeavePath(undefined);
      clearEditSession();
      navigate(path);
    },
    [allowNextNavigation, clearEditSession, navigate]
  );

  const requestLeave = useCallback(
    (path: string) => {
      if (isEditing && hasUnsavedChanges) {
        setPendingLeavePath(path);
        setConfirmDiscard(true);
        return;
      }
      leaveTo(path);
    },
    [hasUnsavedChanges, isEditing, leaveTo]
  );

  const onBeginEdit = useCallback(() => {
    if (!latestActivatedPlantProfile?.profile || !editPath) {
      return;
    }
    initializeEditSession(latestActivatedPlantProfile);
    navigate(withPlantProfileTabQuery(editPath, PlantProfileTab.ALL));
  }, [editPath, initializeEditSession, latestActivatedPlantProfile, navigate]);

  const onCancel = useCallback(() => {
    if (!viewPath) {
      return;
    }
    requestLeave(withPlantProfileTabQuery(viewPath, tabForViewLeave));
  }, [requestLeave, tabForViewLeave, viewPath]);

  const onBack = useCallback(() => {
    if (!parentLink) {
      return;
    }
    requestLeave(parentLink);
  }, [parentLink, requestLeave]);

  const isCategorizedDirty = useMemo(() => {
    if (!isEditing || !draftBaselineProfile || !unsavedPlantProfile) {
      return false;
    }
    const baseline = toUnsavedPlantProfile(draftBaselineProfile);
    if (!baseline) {
      return false;
    }
    return (
      !isEqual(baseline.categories, unsavedPlantProfile.categories, {
        orderedArrays: false,
      }) || !isEqual(baseline.categoryRoles, unsavedPlantProfile.categoryRoles)
    );
  }, [draftBaselineProfile, isEditing, unsavedPlantProfile]);

  const onAlmanacChange = useCallback(
    (categoryId: string, update: AlmanacUpdate) => {
      if (!unsavedPlantProfile) {
        return;
      }
      const currentAlmanac = unsavedPlantProfile.categories.find(
        ({ id }) => id === categoryId
      )?.almanac;
      if (!isValidAlmanac(currentAlmanac)) {
        dispatch(
          addNotification({
            message: t("components.categoryTemplates.errors.invalidAlmanac"),
            variant: "error",
          })
        );
        return;
      }
      setUnsavedPlantProfile({
        ...unsavedPlantProfile,
        categories: unsavedPlantProfile.categories.map((item) =>
          item.id === categoryId
            ? {
                ...item,
                almanac: {
                  ...currentAlmanac,
                  sizeBoundariesMm:
                    update.sizeBoundariesMm ?? currentAlmanac.sizeBoundariesMm,
                  formulas: update.formulas ?? currentAlmanac.formulas,
                },
              }
            : item
        ),
      });
    },
    [dispatch, t, unsavedPlantProfile]
  );

  const clearInvalidAlmanacFieldsForCategory = useCallback(
    (categoryId: string) => {
      const prefix = `${categoryId}:`;
      setInvalidAlmanacFieldIds((prev) => {
        const next = new Set(prev);
        for (const fieldId of prev) {
          if (fieldId.startsWith(prefix)) {
            next.delete(fieldId);
          }
        }
        return next;
      });
    },
    []
  );

  const savedDraftCategories = useMemo(
    () =>
      draftBaselineProfile
        ? toUnsavedPlantProfile(draftBaselineProfile)?.categories
        : undefined,
    [draftBaselineProfile]
  );

  const onAlmanacReset = useCallback(
    (categoryId: string) => {
      const baselineAlmanac = unsavedPlantProfile
        ? tryGetResettableAlmanacBaseline(
            categoryId,
            unsavedPlantProfile.categories,
            savedDraftCategories
          )
        : undefined;
      if (!baselineAlmanac) {
        return;
      }
      setUnsavedPlantProfile((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          categories: prev.categories.map((category) =>
            category.id === categoryId
              ? {
                  ...category,
                  almanac: structuredClone(baselineAlmanac),
                }
              : category
          ),
        };
      });
      clearInvalidAlmanacFieldsForCategory(categoryId);
    },
    [
      clearInvalidAlmanacFieldsForCategory,
      savedDraftCategories,
      unsavedPlantProfile,
    ]
  );

  const onAlmanacValidityChange = useCallback(
    (categoryId: string, fieldId: string, isValid: boolean) => {
      const key = `${categoryId}:${fieldId}`;
      setInvalidAlmanacFieldIds((prev) => {
        const has = prev.has(key);
        if (isValid && !has) {
          return prev;
        }
        if (!isValid && has) {
          return prev;
        }
        const next = new Set(prev);
        if (isValid) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    },
    []
  );

  const isAlmanacResettable = useCallback(
    (categoryId: string): boolean => {
      if (!draftBaselineProfile || !unsavedPlantProfile) {
        return false;
      }
      return Boolean(
        tryGetResettableAlmanacBaseline(
          categoryId,
          unsavedPlantProfile.categories,
          savedDraftCategories
        )
      );
    },
    [draftBaselineProfile, savedDraftCategories, unsavedPlantProfile]
  );

  const draftBaselineVersionId = draftBaselineProfile?.metadata?.versionId;
  // out of date is when server has a newer version than what the draft was derived from.
  const isOutOfDate = Boolean(
    isEditing &&
      draftBaselineVersionId &&
      latestActivatedPlantProfile?.metadata?.versionId &&
      latestActivatedPlantProfile.metadata.versionId !== draftBaselineVersionId
  );

  const refreshToLatest = useCallback(() => {
    if (!latestActivatedPlantProfile) {
      return;
    }
    initializeEditSession(latestActivatedPlantProfile);
  }, [initializeEditSession, latestActivatedPlantProfile]);

  // mutations
  const [applyPlantProfileChanges, { isLoading: isSaving }] = useMutationPopups(
    useSetMutation(),
    {
      success: t("components.categoryCollectionProfile.actions.savedLong", {
        subject: t(
          "models.categoryCollectionProfiles.categoryCollectionProfile_title_one"
        ),
      }),
      portalErrorMessagesByCode: {
        [CLIENT_ERROR_CODES.STALE_VERSION]: t(
          "components.categoryCollectionProfile.warnings.saveStaleConflict"
        ),
      },
    }
  );
  const [revertPlantProfileVersion, { isLoading: isReverting }] =
    useMutationPopups(useSetMutation(), {
      success: t("utils.actions.restoredLong", {
        subject: t(
          "models.categoryCollectionProfiles.categoryCollectionProfile_title_one"
        ),
      }),
    });

  const persistPlantProfile = useCallback(async (): Promise<
    SavedPlantProfile | undefined
  > => {
    if (
      !unsavedPlantProfile ||
      !draftBaselineProfile ||
      !hasUnsavedChanges ||
      hasInvalidAlmanacFields ||
      isSaving ||
      isOutOfDate
    ) {
      return;
    }
    const result = await applyPlantProfileChanges(
      toApplyChangesInput(
        unsavedPlantProfile.id,
        determinePlantProfileChanges(draftBaselineProfile, unsavedPlantProfile)
      )
    );
    return result.data;
  }, [
    applyPlantProfileChanges,
    draftBaselineProfile,
    hasInvalidAlmanacFields,
    hasUnsavedChanges,
    isOutOfDate,
    isSaving,
    toApplyChangesInput,
    unsavedPlantProfile,
  ]);

  const handleSavePlantProfile = useCallback(async () => {
    const saved = await persistPlantProfile();
    if (saved) {
      initializeEditSession(saved);
    }
  }, [initializeEditSession, persistPlantProfile]);

  const handleSaveAndClosePlantProfile = useCallback(async () => {
    if (!viewPath) {
      return;
    }
    const saved = await persistPlantProfile();
    if (saved) {
      leaveTo(withPlantProfileTabQuery(viewPath, tabForViewLeave));
    }
  }, [leaveTo, persistPlantProfile, tabForViewLeave, viewPath]);

  const [deletePlantProfile] = useMutationPopups(useSetMutation(), {
    success: t("utils.actions.deletedLong", {
      subject: t(
        "models.categoryCollectionProfiles.categoryCollectionProfile_title_one"
      ),
    }),
  });

  // visible state
  const profileId = displayProfile?.id;
  const isActiveOnRobot = Boolean(
    profileId && robotSummary && isPlantProfileActive(profileId, robotSummary)
  );
  const isActiveOnFleet = Boolean(
    profileId &&
      customerUuid &&
      !robotSummary &&
      isPlantProfileActiveOnFleet(profileId, robotSummaries, customerUuid)
  );
  const isActive = isActiveOnRobot || isActiveOnFleet;
  const isDeleteDisabled = shouldScanFleet && isFleetLoading && !isFleetError;

  // categories
  const categoryOptions = useMemo(() => {
    const categories = displayProfile
      ? orderCategoriesByRole(
          displayProfile.categories,
          displayProfile.categoryRoles
        )
      : [];
    const chips = toRadioChip(i18n, t, categories, false);
    if (!displayProfile) {
      return chips;
    }
    return chips.map((option) => {
      const category = displayProfile.categories.find(
        ({ id }) => id === option.id
      );
      return {
        ...option,
        icon: (
          <PlantProfileRoleIcon
            role={resolveCategoryRole(
              option.id,
              category?.name,
              displayProfile.categoryRoles
            )}
            fontSize="small"
          />
        ),
      };
    });
  }, [displayProfile, i18n, t]);
  const categorizationMap = useMemo(
    () => imagesToCategoryIds(displayProfile?.categories),
    [displayProfile]
  );
  const createCategoryRadioChipLabel = useCallback(
    ({ id, name }: RadioChipOption): string => {
      if (!displayProfile) {
        return "";
      }
      const match = findWhere(displayProfile.categories, { id });
      if (!match) {
        return name;
      }
      const { chipIds } = match;
      const count = chipIds.length > 0 ? ` (${chipIds.length})` : "";
      return `${name}${count}`;
    },
    [displayProfile]
  );

  const onImageToggle = useCallback(
    (predictionPointId?: string, categoryId?: string) => {
      if (!isEditing || !unsavedPlantProfile || isNil(predictionPointId)) {
        return;
      }
      setUnsavedPlantProfile(
        plantProfileImageToggle(
          unsavedPlantProfile,
          categoryId,
          predictionPointId
        )
      );
    },
    [isEditing, unsavedPlantProfile]
  );

  const onRevert = useCallback(
    (revertToVersionId: string) => {
      const baseline = isEditing
        ? draftBaselineProfile
        : latestActivatedPlantProfile;
      const profileId = baseline?.profile?.id;
      const versionId = baseline?.metadata?.versionId;
      if (!profileId || !versionId) {
        return Promise.resolve();
      }
      return revertPlantProfileVersion(
        toApplyChangesInput(
          profileId,
          revertPlantProfileChanges(profileId, versionId, revertToVersionId)
        )
      ).then((result) => {
        if (result.data && isEditing) {
          initializeEditSession(result.data);
        }
        return result;
      });
    },
    [
      draftBaselineProfile,
      initializeEditSession,
      isEditing,
      latestActivatedPlantProfile,
      revertPlantProfileVersion,
      toApplyChangesInput,
    ]
  );

  const onReviveFromView = useCallback(
    async (predictionPointId?: string, categoryId?: string) => {
      if (
        !latestActivatedPlantProfile?.profile ||
        isNil(predictionPointId) ||
        isNil(categoryId)
      ) {
        return;
      }
      const baseline = toUnsavedPlantProfile(latestActivatedPlantProfile);
      if (!baseline) {
        return;
      }
      const updated = plantProfileImageToggle(
        baseline,
        categoryId,
        predictionPointId
      );
      await applyPlantProfileChanges(
        toApplyChangesInput(
          updated.id,
          determinePlantProfileChanges(latestActivatedPlantProfile, updated)
        )
      );
    },
    [applyPlantProfileChanges, latestActivatedPlantProfile, toApplyChangesInput]
  );

  // permissions
  const canRead = useCanReadPlantProfile(isAdmin);
  const canUpdate =
    useCanUpdatePlantProfile(isAdmin) &&
    (!(isEditing
      ? draftBaselineProfile?.profile?.carbon
      : latestActivatedPlantProfile?.profile?.carbon) ||
      isAdmin);

  const showRobotContextAlerts = Boolean(robotSummary);

  if (!uuid && parentLink) {
    return <Navigate to={parentLink} replace />;
  }
  if (!uuid || !canRead) {
    return;
  }

  if (isPlantProfileLoading || !displayProfile) {
    return <Loading failed={isError} error={error} />;
  }

  if (isEditing && !canUpdate && viewPath) {
    return (
      <Navigate
        to={withPlantProfileTabQuery(viewPath, PlantProfileTab.CATEGORIZED)}
        replace
      />
    );
  }

  const currentVersionId =
    savedPlantProfileForCompare?.metadata?.versionId ??
    latestActivatedPlantProfile?.metadata?.versionId;

  const isSaveDisabled =
    !hasUnsavedChanges || hasInvalidAlmanacFields || isSaving || isOutOfDate;
  const plantProfileEditShared = unsavedPlantProfile
    ? {
        unsavedPlantProfile,
        setUnsavedPlantProfile,
        activeCategoryId,
        setActiveCategoryId,
        categoryOptions,
        categorizationMap,
        createCategoryRadioChipLabel,
        isPlantProfileLoading,
        onImageToggle,
        isCategorizedDirty,
        onAlmanacChange,
        onAlmanacReset,
        onAlmanacValidityChange,
        onClearInvalidAlmanacFields: clearInvalidAlmanacFieldsForCategory,
        isAlmanacResettable,
        floatingSaveAction: (
          <FloatingSaveButton
            onClick={handleSavePlantProfile}
            disabled={isSaveDisabled}
            loading={isSaving}
          />
        ),
      }
    : undefined;

  return (
    <div className="flex flex-col min-h-full">
      {canUpdate && isAdmin && (
        <Alert severity="warning" className="mb-8">
          {t("components.categoryCollectionProfile.warnings.admin")}
        </Alert>
      )}
      {isActive && !showRobotContextAlerts && !isFleetLoading && (
        <Alert severity="warning" className="mb-8">
          {t("components.categoryCollectionProfile.warnings.production")}
        </Alert>
      )}
      {latestActivatedPlantProfile?.profile?.carbon && !isAdmin && (
        <Alert severity="warning" className="mb-8">
          {t("components.categoryCollectionProfile.warnings.protected")}
        </Alert>
      )}
      {isOutOfDate && (
        <OutOfDateAlert
          hasUnsavedChanges={hasUnsavedChanges}
          onRefresh={refreshToLatest}
        />
      )}
      {/* Header */}
      <div className="flex flex-wrap flex-col md:flex-row items-start md:items-center justify-center md:justify-between md:mb-8 w-full gap-2 md:gap-4">
        {/* Left Controls */}
        <div className="flex items-center justify-start flex-none">
          {/* Breadcrumb */}
          {parentLink && (
            <IconButton
              onClick={onBack}
              aria-label={t("utils.actions.backShort")}
            >
              <ParentIcon />
            </IconButton>
          )}
        </div>
        {/* Title */}
        <div className="flex flex-auto items-center gap-2 min-w-0">
          {isEditing && unsavedPlantProfile ? (
            <PlantProfileEditableHeader
              unsavedPlantProfile={unsavedPlantProfile}
              customerUuid={customerUuid}
              setUnsavedPlantProfile={setUnsavedPlantProfile}
              setActiveCategoryId={setActiveCategoryId}
            />
          ) : (
            <Typography variant="h4">{displayProfile.name}</Typography>
          )}
        </div>
        {/* Right Controls */}
        <div className="flex items-center gap-4 flex-none">
          {isEditing ? (
            <>
              <Button
                color="inherit"
                variant="outlined"
                onClick={onCancel}
                disabled={isSaving}
              >
                {t("utils.actions.cancel")}
              </Button>
              <Button
                color="primary"
                variant="contained"
                loading={isSaving}
                onClick={handleSavePlantProfile}
                disabled={isSaveDisabled}
                startIcon={<SaveIcon />}
              >
                {t("utils.actions.save")}
              </Button>
              <Button
                color="primary"
                variant="contained"
                loading={isSaving}
                onClick={handleSaveAndClosePlantProfile}
                disabled={isSaveDisabled}
              >
                {t("utils.actions.saveAndClose")}
              </Button>
            </>
          ) : (
            canUpdate && (
              <>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={onBeginEdit}
                  startIcon={<EditIcon />}
                >
                  {t("utils.actions.edit")}
                </Button>
                <Tooltip
                  arrow
                  title={
                    isActive
                      ? t(
                          "components.categoryCollectionProfile.warnings.cannotDeleteActive"
                        )
                      : isDeleteDisabled
                      ? t(
                          "components.categoryCollectionProfile.warnings.cannotDeleteChecking"
                        )
                      : ""
                  }
                >
                  <span>
                    <Button
                      color="error"
                      variant="contained"
                      onClick={() => setConfirmDelete(true)}
                      disabled={isActive || isDeleteDisabled}
                      startIcon={<DeleteIcon />}
                    >
                      {t("utils.actions.delete")}
                    </Button>
                  </span>
                </Tooltip>
              </>
            )
          )}
          {confirmDelete && (
            <ConfirmationDialog
              title={t("utils.actions.deleteLong", {
                subject: t(
                  "models.categoryCollectionProfiles.categoryCollectionProfile_title_one"
                ),
              })}
              description={t(
                "components.ConfirmationDialog.delete.description",
                {
                  subject: t(
                    "models.categoryCollectionProfiles.categoryCollectionProfile_title_one"
                  ),
                }
              )}
              destructive
              yesText={t("utils.actions.delete", {
                subject: displayProfile.name,
              })}
              onClose={() => setConfirmDelete(false)}
              onYes={async () => {
                const result = await deletePlantProfile(
                  toApplyChangesInput(
                    displayProfile.id,
                    deletePlantProfileChanges(displayProfile)
                  )
                );
                if (parentLink && !result.error) {
                  navigate(parentLink);
                }
              }}
            />
          )}
          {confirmDiscard && (
            <UnsavedChangesDiscardDialog
              onClose={() => {
                setConfirmDiscard(false);
                setPendingLeavePath(undefined);
              }}
              onYes={() => {
                if (!isUndefined(pendingLeavePath)) {
                  leaveTo(pendingLeavePath);
                }
              }}
            />
          )}
          {isNavigationBlocked && (
            <UnsavedChangesDiscardDialog
              onClose={resetBlockedNavigation}
              onYes={() => {
                clearEditSession();
                proceedWithNavigation();
              }}
            />
          )}
        </div>
      </div>
      {/* Main View */}
      {/* Note: overflow-visible is required for making the options sticky */}
      <Paper className="p-4 pr-8 mt-4 overflow-visible">
        <CategoryCollectionCustomerProvider customerId={customerUuid}>
          {isEditing && plantProfileEditShared && isAdmin ? (
            <PlantProfileEdit isAdmin {...plantProfileEditShared} />
          ) : isEditing && plantProfileEditShared && customerUuid ? (
            <PlantProfileEdit
              customerUuid={customerUuid}
              serial={robotSummary?.robot?.serial}
              {...plantProfileEditShared}
            />
          ) : (
            <PlantProfileView
              uuid={uuid}
              plantProfile={displayProfile}
              categoryOptions={categoryOptions}
              categorizationMap={categorizationMap}
              currentVersionId={currentVersionId}
              customerUuid={customerUuid}
              onRevert={canUpdate ? onRevert : undefined}
              isReverting={isReverting}
              onRevive={canUpdate ? onReviveFromView : undefined}
            />
          )}
        </CategoryCollectionCustomerProvider>
      </Paper>
    </div>
  );
};
